import {
  AlertCircle,
  Check,
  CheckCircle2,
  Clock3,
  CookingPot,
  RotateCw,
  ShoppingBag,
  X,
  XCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import DashboardStatusBadge from "./DashboardStatusBadge";

import { formatPersianNumber as numberFormatter } from "../../utils/persianNumber";
const dateTimeFormatter = new Intl.DateTimeFormat("fa-IR", { hour: "2-digit", minute: "2-digit" });
const dateFormatter = new Intl.DateTimeFormat("fa-IR", { month: "short", day: "numeric" });

const statusConfig = {
  pending: { label: "سفارش جدید", icon: Clock3, className: "bg-amber-50 text-amber-800" },
  processing: { label: "در حال آماده‌سازی", icon: CookingPot, className: "bg-blue-50 text-blue-700" },
  completed: { label: "تکمیل شده", icon: CheckCircle2, className: "bg-secondary_1 text-secondary" },
  canceled: { label: "لغو شده", icon: XCircle, className: "bg-gray-100 text-gray-700" },
};

const toPersianNumber = (value) => numberFormatter(Number(value) || 0);

const orderDate = (order) => {
  const date = new Date(order.createdAt);
  if (Number.isNaN(date.getTime())) return { date: "", time: "—" };
  return { date: dateFormatter.format(date), time: dateTimeFormatter.format(date) };
};

const OrderStatus = ({ order }) => {
  const config = statusConfig[order.status] || {
    label: order.status || "نامشخص",
    icon: AlertCircle,
    className: "bg-gray-100 text-gray-700",
  };
  return <DashboardStatusBadge {...config} />;
};

const OrderActions = ({ order, busyKey, onStatusChange }) => {
  if (order.status !== "pending") return null;

  const actions = [
    { status: "completed", label: "تایید", icon: Check },
    { status: "canceled", label: "لغو", icon: X, destructive: true },
  ];

  const runAction = (action) => {
    if (action.destructive && !window.confirm("آیا از لغو این سفارش مطمئن هستید؟")) return;
    onStatusChange(order._id, action.status);
  };

  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      {actions.map((action) => {
        const Icon = action.icon;
        const actionKey = `order:${order._id}:${action.status}`;
        const busy = busyKey === actionKey;
        return (
          <button
            key={action.status}
            type="button"
            onClick={() => runAction(action)}
            disabled={Boolean(busyKey)}
            className={`inline-flex min-h-9 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-bold transition-colors focus:outline-none focus:ring-4 disabled:cursor-wait disabled:opacity-50 ${
              action.destructive
                ? "border-red-100 text-red-700 hover:bg-red-50 focus:ring-red-50"
                : "border-secondary/20 text-secondary hover:bg-secondary_1 focus:ring-secondary_1"
            }`}
          >
            {busy ? <RotateCw size={14} className="animate-spin" aria-hidden="true" /> : <Icon size={14} aria-hidden="true" />}
            {action.label}
          </button>
        );
      })}
    </div>
  );
};

const LoadingRows = () => (
  <div className="space-y-2" aria-label="در حال بارگذاری سفارش‌های اخیر" aria-busy="true">
    {[0, 1, 2].map((item) => (
      <div key={item} className="h-16 animate-pulse rounded-xl bg-gray-100" />
    ))}
  </div>
);

export default function RecentOrdersSection({
  orders,
  loading,
  error,
  actionError,
  busyKey,
  onRetry,
  onStatusChange,
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white" aria-labelledby="recent-orders-title">
      <header className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <h2 id="recent-orders-title" className="flex items-center gap-2 text-sm font-extrabold text-accent">
          <ShoppingBag size={16} className="text-primary" aria-hidden="true" />
          سفارش‌های اخیر
        </h2>
        <Link to="/dashboard/orders" className="shrink-0 text-xs font-bold text-primary hover:underline">
          همه سفارش‌ها
        </Link>
      </header>

      <div className="p-3">
        {loading ? (
          <LoadingRows />
        ) : error ? (
          <div className="flex min-h-40 flex-col items-center justify-center rounded-xl bg-red-50/70 p-4 text-center">
            <AlertCircle size={22} className="text-red-600" aria-hidden="true" />
            <p className="mt-2 text-sm font-bold text-red-800">دریافت سفارش‌ها ناموفق بود</p>
            <button type="button" onClick={onRetry} className="mt-3 text-xs font-bold text-red-700 underline">تلاش دوباره</button>
          </div>
        ) : orders.length === 0 ? (
          <div className="flex min-h-40 flex-col items-center justify-center rounded-xl bg-gray-50 p-4 text-center">
            <ShoppingBag size={24} className="text-gray-400" aria-hidden="true" />
            <p className="mt-2 text-sm font-bold text-gray-700">هنوز سفارشی ثبت نشده است</p>
            <p className="mt-1 text-xs text-gray-500">سفارش‌های جدید به‌صورت زنده در این بخش ظاهر می‌شوند.</p>
          </div>
        ) : (
          <>
            {actionError && (
              <p className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-xs font-medium text-red-700" role="alert">{actionError}</p>
            )}

            <div className="hidden lg:block" role="table" aria-label="سفارش‌های اخیر">
              <div className="grid grid-cols-[5rem_5.5rem_minmax(6rem,1fr)_7rem_8rem_minmax(8rem,1fr)] gap-2 border-b border-gray-100 px-2 pb-2 text-xs font-bold text-gray-400" role="row">
                <span role="columnheader">سفارش</span>
                <span role="columnheader">زمان</span>
                <span role="columnheader">نوع سفارش</span>
                <span role="columnheader">مبلغ</span>
                <span role="columnheader">وضعیت</span>
                <span role="columnheader" className="text-left">عملیات</span>
              </div>
              <div className="divide-y divide-gray-100">
                {orders.map((order) => {
                  const createdAt = orderDate(order);
                  return (
                    <div key={order._id} className="grid min-h-16 grid-cols-[5rem_5.5rem_minmax(6rem,1fr)_7rem_8rem_minmax(8rem,1fr)] items-center gap-2 px-2 py-2.5 text-sm" role="row">
                      <p role="cell" className="font-extrabold text-accent">#{toPersianNumber(order.orderNumber)}</p>
                      <div role="cell"><p className="font-bold text-gray-700">{createdAt.time}</p><p className="mt-0.5 text-[11px] text-gray-400">{createdAt.date}</p></div>
                      <p role="cell" className="truncate text-gray-600">{order.tableNumber ? `میز ${toPersianNumber(order.tableNumber)}` : "بیرون‌بر"}</p>
                      <p role="cell" className="whitespace-nowrap text-gray-700">{toPersianNumber(order.totalPrice)} تومان</p>
                      <div role="cell"><OrderStatus order={order} /></div>
                      <div role="cell"><OrderActions order={order} busyKey={busyKey} onStatusChange={onStatusChange} /></div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2 lg:hidden">
              {orders.map((order) => {
                const createdAt = orderDate(order);
                return (
                  <article key={order._id} className="rounded-xl border border-gray-100 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-extrabold text-accent">سفارش #{toPersianNumber(order.orderNumber)}</p>
                        <p className="mt-1 text-xs text-gray-400">{createdAt.time} · {createdAt.date}</p>
                      </div>
                      <OrderStatus order={order} />
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3 text-sm">
                      <span className="text-gray-600">{order.tableNumber ? `میز ${toPersianNumber(order.tableNumber)}` : "بیرون‌بر"}</span>
                      <strong className="text-gray-800">{toPersianNumber(order.totalPrice)} تومان</strong>
                    </div>
                    <div className="mt-3 border-t border-gray-100 pt-3">
                      <OrderActions order={order} busyKey={busyKey} onStatusChange={onStatusChange} />
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
